import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ProjectUpgradeFees } from '@freelancer/datastore/collections';
import { ListItemInputAlignment, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight } from '@freelancer/ui/text';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';

@Component({
  selector: 'app-project-advanced-upgrades',
  template: `
    <fl-bit [flMarginBottom]="Margin.LARGE">
      <fl-list
        [inputAlignment]="ListItemInputAlignment.TOP"
        [type]="ListItemType.CHECKBOX"
      >
        <fl-list-item
          *ngIf="showRecruiterUpgrade"
          [control]="control.get('recruiter')"
          [flTrackingLabel]="
            control.get('recruiter')?.value
              ? 'Recruiter-Check'
              : 'Recruiter-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - assisted upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeAssisted"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.assistedFullPrice"
            [promoPrice]="upgradeFees?.assistedPrice"
            [upgradeType]="UpgradeType.ASSISTED"
          >
            One of our experts will find and recommend the perfect freelancer
            for your job, saving you time. The best experience for new users.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          [control]="control.get('nda')"
          [flTrackingLabel]="
            control.get('nda')?.value ? 'NDA-Check' : 'NDA-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - NDA upgrade description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeNda"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.ndaPrice"
            [upgradeType]="UpgradeType.NDA"
          >
            Freelancers must sign a Non-disclosure Agreement to work on your
            project. Freelancers agree to keep details discussed through private
            messages and files confidential.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          [control]="control.get('featured')"
          [flTrackingLabel]="
            control.get('featured')?.value
              ? 'Featured-Check'
              : 'Featured-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - featured upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeFeatured"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.featuredPrice"
            [upgradeType]="UpgradeType.FEATURED"
          >
            Attract more freelancers with a prominent placement in our 'Featured
            Jobs and Contests' page.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          [control]="control.get('urgent')"
          [flTrackingLabel]="
            control.get('urgent')?.value ? 'Urgent-Check' : 'Urgent-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - urgent upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeUrgent"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.urgentPrice"
            [upgradeType]="UpgradeType.URGENT"
          >
            Make your project stand out and let freelancers know that your job
            is time sensitive.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          [control]="control.get('private')"
          [flTrackingLabel]="
            control.get('private')?.value ? 'Private-Check' : 'Private-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - private upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freePrivate"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.nonPublicPrice"
            [upgradeType]="UpgradeType.PRIVATE"
          >
            Hide project details from search engines and users that are not
            logged in, for projects that you need to keep confidential.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          *ngIf="upgradeFees?.freeIpContract"
          [clickable]="true"
          [control]="control.get('ipContract')"
          [flTrackingLabel]="
            control.get('ipContract')?.value
              ? 'IPContract-Check'
              : 'IPContract-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - IP contract upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeIpContract"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.ipContractPrice"
            [upgradeType]="UpgradeType.IP_AGREEMENT"
          >
            Do you need ownership? This upgrade will require your Freelancer to
            sign an Intellectual Property (IP) Agreement. This will prove that
            all the work done belongs to you.
          </fl-upgrade-item>
        </fl-list-item>

        <fl-list-item
          *ngIf="upgradeFees?.freeSealed"
          [clickable]="true"
          [control]="control.get('sealed')"
          [flTrackingLabel]="
            control.get('sealed')?.value ? 'Sealed-Check' : 'Sealed-Uncheck'
          "
        >
          <fl-upgrade-item
            i18n="
               Post Job Page project advanced upgrade - sealed upgrade
              description
            "
            [currency]="upgradeFees?.currency"
            [freeUpgrade]="upgradeFees?.freeSealed"
            [isLoadingPrice]="isLoadingFees"
            [price]="upgradeFees?.sealedPrice"
            [upgradeType]="UpgradeType.SEALED"
          >
            Do you want higher-quality proposals? This upgrade will hide a
            Freelancer's bid from other Freelancers. This will result in
            accurate and unique proposals for your project.
          </fl-upgrade-item>
        </fl-list-item>
      </fl-list>
    </fl-bit>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectAdvancedUpgradesComponent {
  FontWeight = FontWeight;
  ListItemInputAlignment = ListItemInputAlignment;
  ListItemType = ListItemType;
  Margin = Margin;
  UpgradeType = UpgradeType;

  @Input() control: FormControl;
  @Input() isLoadingFees: boolean;
  @Input() showAdvanced: boolean;
  @Input() showRecruiterUpgrade: boolean;
  @Input() upgradeFees: ProjectUpgradeFees;
}
